import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, logger } from "../lib/api";
import { useWishlist } from "../context/WishlistContext";
import { ProductCard } from "../components/ProductCard";
import { FadeIn } from "../components/FadeIn";
import { toast } from "sonner";

const WishlistSkeleton = () => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
    {["wl-skeleton-1", "wl-skeleton-2"].map((k) => (
      <div key={k} className="aspect-[3/4] bg-capra-surface animate-pulse" />
    ))}
  </div>
);

const WishlistEmpty = () => (
  <div className="text-center py-20" data-testid="wishlist-empty">
    <p className="font-display text-xl text-capra-ivory mb-3">Nothing saved yet.</p>
    <p className="text-sm text-capra-muted mb-8">Mark the pieces that speak to you.</p>
    <Link
      to="/new-collection"
      className="inline-block px-9 py-4 border border-capra-bronze/60 text-capra-bronze text-[11px] uppercase tracking-[0.25em] hover:bg-capra-bronze hover:text-capra-night transition-colors"
    >
      Explore Collection
    </Link>
  </div>
);

const WishlistGrid = ({ products }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10" data-testid="wishlist-grid">
    {products.map((p) => (
      <ProductCard key={p.id} product={p} />
    ))}
  </div>
);

export default function WishlistPage() {
  const { ids } = useWishlist();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/products");
        setProducts(data.filter((p) => ids.includes(p.id)));
      } catch (err) {
        logger.error("Wishlist products fetch failed", err);
        toast.error("Unable to load your wishlist. Please try again.");
      }
      setLoading(false);
    };
    fetchAll();
  }, [ids]);

  let content;
  if (loading) {
    content = <WishlistSkeleton />;
  } else if (products.length === 0) {
    content = <WishlistEmpty />;
  } else {
    content = <WishlistGrid products={products} />;
  }

  return (
    <div className="pt-36 lg:pt-44 pb-24 px-6 lg:px-10 max-w-[1600px] mx-auto" data-testid="wishlist-page">
      <FadeIn>
        <p className="overline-label mb-4">Saved Pieces</p>
        <h1 className="font-display text-4xl sm:text-5xl text-capra-ivory mb-12">Your Wishlist</h1>
      </FadeIn>
      {content}
    </div>
  );
}
